<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-2 text-left">
      <button
        data-test="open-claim"
        type="button"
        class="btn-icon btn-icon-default"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <template v-if="trade">
        <p class="w-60">
          {{ trade.name }}
        </p>
      </template>

      <template v-else>
        <p>Package</p>
      </template>
    </td>

    <td
      class="group"
      :title="showBudgetCommentTooltip ? varianceComment.body : ''"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showBudgetCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          aria-label="Edit comment"
          @click.stop="editBudgetComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ australianCurrency(totalCosts.budget) }}
        </div>
      </div>
    </td>

    <td class="pr-3" />

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td
      class="group bg-green-200/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 && varianceComment?.body ? varianceComment.body : ''"
    >
      <div class="flex items-center justify-end">
        <button
          v-if="showCommentButton"
          type="button"
          class="btn-icon group-hover:visible btn-icon-default invisible mr-1 shrink-0"
          aria-label="Edit variance comment"
          @click.stop="editVarianceComment"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
    />
</svg>
        </button>

        <div class="min-w-0">
          {{ australianCurrency(totalCosts.currentClaim - totalCosts.currentCertified) }}
        </div>
      </div>
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ isNumber(totalCosts.currentClaim / totalCosts.budget) ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ isNumber(totalCosts.currentCertified / totalCosts.budget) ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ isNumber(totalCosts.previous / totalCosts.budget) ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ isNumber(totalCosts.cumulative / totalCosts.budget) ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td class="pr-3">
      {{ australianCurrency(Math.abs(totalCosts.budget - totalCosts.cumulative)) }}
    </td>

    <td
      class="bg-orange-200/30 relative z-10"
    >
      <span v-if="showForecasts">{{ australianCurrency(totalCosts.baselineForecast) }}</span>
    </td>

    <td
      v-if="showForecasts"
      class="bg-orange-200/30 relative z-10 pr-3"
    >
      <p>
        {{ australianCurrency(totalCosts.actualForecast) }}
      </p>

      <p
        v-if="totalCosts.previousActualForecast !== 0"
        class="text-xs"
        :class="[totalCosts.actualForecast - totalCosts.previousActualForecast <= 0 ? 'text-green-600' : 'text-red-600']"
      >
        {{ australianCurrency(totalCosts.actualForecast - totalCosts.previousActualForecast) }} m-1
      </p>
    </td>
  </tr>

  <template v-if="open">
    <RatesTradeRowItem
      v-for="item in rootTradeItems"
      :key="item.tradeItem.id"
      :trade="$props.trade"
      :claim="$props.claim"
      :trade-item="item.tradeItem"
      :project-id="projectId"
      :package-id="packageId"
      :can-edit-certified="canEditCertified"
      :lite-mode="props.liteMode"
      :show-percentages="$props.showPercentages"
      :show-forecasts="$props.showForecasts"
    />
  </template>
</template>

<script setup lang="ts">
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber, falsey, isNumber } from '@/helpers';
import { t } from '@/i18n';
import Claim, { ClaimStatus } from '@/models/Claim';
import PackageTradeItem from '@/models/PackageTradeItem';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useCommentsStore } from '@/store/comments';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useTradesStore } from '@/store/trades';
import Swal from 'sweetalert2';
import { computed, ref } from 'vue';
import { toast } from 'vue3-toastify';
import RatesTradeRowItem from './RatesTradeRowItem.vue';

const props = defineProps<{
  trade?: Trade;
  claim: Claim;
  projectId: string;
  packageId: string;
  canEditCertified: boolean;
  packageTradeItems: PackageTradeItem[];
  liteMode?: boolean;
  showPercentages?: boolean;
  showForecasts?: boolean;
}>();

const commentsStore = useCommentsStore();
const tradesStore = useTradesStore();
const tradeItemsStore = useTradeItemsStore();
const updateCommentAction = useStoreApiAction(tradesStore.updateComment);

const getRootTradeItem = (tradeItem: TradeItem): TradeItem => {
  let current = tradeItemsStore.models.find(tradeItem.id) ?? tradeItem;

  while(current.parent_id) {
    const parent = tradeItemsStore.models.find(current.parent_id);

    if(!parent) {
      break;
    }

    current = parent;
  }

  return current;
};

const rootTradeItems = computed(() => {
  const roots = new Map<number, { tradeItem: TradeItem; packageTradeItem: PackageTradeItem }>();

  [...props.packageTradeItems]
    .sort((a, b) => a.sort_order - b.sort_order)
    .forEach((packageTradeItem) => {
      if(!packageTradeItem.trade_item) {
        return;
      }

      const root = getRootTradeItem(packageTradeItem.trade_item);

      if(!roots.has(root.id)) {
        roots.set(root.id, { tradeItem: root, packageTradeItem });
      }
    });

  return Array.from(roots.values());
});

const totalCosts = computed(() => {
  const totals = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
    baselineForecast: 0,
    actualForecast: 0,
    previousActualForecast: 0,
  };

  props.packageTradeItems.forEach((packageTradeItem) => {
    totals.budget += packageTradeItem.value;
    totals.previous += packageTradeItem.previous(props.claim) * packageTradeItem.rate;
    totals.baselineForecast += packageTradeItem.baseline_forecast_amount || 0;
    totals.actualForecast += packageTradeItem.actualForecastForClaim(props.claim) || 0;
    totals.previousActualForecast += packageTradeItem.previousActualForecastForClaim(props.claim) || 0;

    // Get rates cost for this trade item
    const ratesCost = packageTradeItem.rates_costs[0];

    if(ratesCost) {
      totals.currentClaim += (ratesCost.current_quantity || 0) * packageTradeItem.rate;
      totals.currentCertified += (ratesCost.current_certified || 0) * packageTradeItem.rate;
    }
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});

const open = ref(false);

const varianceComment = computed(() => {
  if(props.trade) {
    return commentsStore.models
      .where('commentable_id', props.trade.id)
      .where('commentable_type', props.trade.$entity())
      .where('creator_id', props.claim.id)
      .where('creator_type', props.claim.$entity())
      .first();
  }

  return undefined;
});

const hasVariance = computed(() => {
  return !falsey(totalCosts.value?.currentClaim) && !falsey(totalCosts.value?.currentCertified);
});

const variance = computed(() => {
  return totalCosts.value?.currentClaim - totalCosts.value?.currentCertified;
});

const showCommentButton = computed(() => {
  return hasVariance.value && variance.value !== 0 && !!varianceComment.value && props.canEditCertified;
});

const showBudgetCommentButton = computed(() => {
  return props.canEditCertified && !!varianceComment.value && props.trade &&
    (!hasVariance.value || variance.value === 0);
});

const showBudgetCommentTooltip = computed(() => {
  return !!varianceComment.value?.body && showBudgetCommentButton.value;
});

const editBudgetComment = () => {
  editVarianceComment();
};

const editVarianceComment = () => {
  Swal.fire({
    icon: 'warning',
    title: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentTitle'),
    text: t('ClaimCertifiedCosts.TradeRowItem.alert.reasonAdjustmentText'),
    input: 'textarea',
    inputValue: varianceComment.value?.body ?? '',
    showCancelButton: true,
    confirmButtonText: 'Update',
  }).then((result) => {
    if(result.isConfirmed) {
      if(result.value) {
        updateCommentAction.request(props.trade.id, varianceComment.value.id, { comment: result.value }).then(() => {
          toast.success('Comment updated');
        });
      }
    }
  });
};
</script>

<style scoped></style>
